/**
 * Shared presentation helpers for the ABF facility workspace.
 *
 * One date style, one money style and real plurals across every tab, so the
 * header, the rails and the panels read as one product. Pure formatting only:
 * nothing here changes what a number means or where it comes from.
 */

const DATE_FMT = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
const DATE_TIME_FMT = new Intl.DateTimeFormat('en-US', {
  month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
});

/** "Sep 30, 2026" from an ISO date or timestamp; the fallback when absent. */
export const fmtDate = (value: string | null | undefined, fallback = '—'): string => {
  if (!value) return fallback;
  const iso = String(value);
  // The calendar day as recorded, never shifted by the browser's zone: a timestamp keeps
  // its own date part, exactly as the facility header always rendered it.
  const day = iso.match(/^(\d{4}-\d{2}-\d{2})/);
  const parsed = day ? new Date(`${day[1]}T00:00:00`) : new Date(iso);
  return Number.isNaN(parsed.getTime()) ? iso : DATE_FMT.format(parsed);
};

/** "Oct 6, 2026, 9:44 PM" from an ISO timestamp, in the viewer's local time. */
export const fmtDateTime = (value: string | null | undefined, fallback = '—'): string => {
  if (!value) return fallback;
  const parsed = new Date(String(value));
  return Number.isNaN(parsed.getTime()) ? String(value) : DATE_TIME_FMT.format(parsed);
};

/** "$22,946,079" — whole units, the header's convention. */
export const fmtMoney = (
  value: number | string | null | undefined,
  currency = 'USD',
  fractionDigits = 0,
  fallback = '—',
): string => {
  if (value === null || value === undefined || value === '') return fallback;
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency, minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits,
  }).format(n);
};

/** "1 control" / "3 controls" — never "control(s)". */
export const plural = (count: number | null | undefined, singular: string, pluralForm?: string): string => {
  const n = Number(count ?? 0);
  const word = n === 1 ? singular : (pluralForm ?? `${singular}s`);
  return `${n.toLocaleString('en-US')} ${word}`;
};

/** The word alone, for sentences that already carry the number. */
export const pluralWord = (count: number | null | undefined, singular: string, pluralForm?: string): string =>
  Number(count ?? 0) === 1 ? singular : (pluralForm ?? `${singular}s`);

/** "NET_LOSS_RATE" → "Net loss rate"; "30_DELINQUENCY" → "30+ delinquency" when a known alias exists. */
const TOKEN_ALIASES: Record<string, string> = {
  '30_DELINQUENCY': '30+ delinquency',
  DELINQUENCY_30: '30+ delinquency',
  OVERCOLLATERALIZATION: 'Overcollateralization',
  INTERNAL_VALUATION_MEMO: 'Internal valuation memo',
};
export const humanizeToken = (value: string | null | undefined): string => {
  if (!value) return '';
  const raw = String(value).trim();
  if (TOKEN_ALIASES[raw]) return TOKEN_ALIASES[raw];
  if (!/^[A-Z0-9_]+$/.test(raw)) return raw;
  const words = raw.toLowerCase().replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/**
 * Business words for the enum tokens that reach a screen (F-LR-052 and the raw-enum findings):
 * consequence keys and types, exception types, roll and freshness states. Anything not listed
 * falls back to humanizeToken with the acronyms kept upright, so "ABF_SERVICER_REPORT" reads
 * "ABF servicer report", never "Abf servicer report".
 */
const LABEL_ALIASES: Record<string, string> = {
  // Contractual consequences
  AVAILABILITY_DRAW_BLOCK: 'Availability hold on new draws',
  DRAW_BLOCK: 'Hold on new draws',
  COVENANT_BREACH: 'Covenant breach',
  WATERFALL_MODE: 'Waterfall mode',
  TRAP_TO_RESERVE: 'Trap cash to the reserve',
  CASH_TRAP: 'Cash trap',
  CASH_SWEEP: 'Cash sweep',
  RESERVE_INCREASE: 'Reserve increase',
  DEFAULT_RATE: 'Default rate of interest',
  EVENT_OF_DEFAULT: 'Event of default',
  TERMINATION_EVENT: 'Termination event',
  SEQUENTIAL: 'Sequential pay',
  REINVESTMENT: 'Reinvestment period',
  DDTL_AVAILABILITY_END: 'Delayed-draw availability ends',
  DDTL_AVAILABILITY_ENDS: 'Delayed-draw availability ends',
  // Exception and attention types
  BBC_VARIANCE: 'Borrowing base certificate variance',
  COVENANT_CERTIFICATE_VARIANCE: 'Compliance certificate variance',
  COVENANT_COVERAGE_GAP: 'Covenant not tested',
  CASH_TO_COLLATERAL: 'Cash does not reconcile to collateral',
  CASH_IN_UNCONTROLLED_ACCOUNT: 'Cash in an uncontrolled account',
  BORROWING_BASE_DROP: 'Borrowing base drop',
  SURVEILLANCE_TRIGGER: 'Surveillance trigger',
  FRAUD_CONTROL: 'Fraud control',
  DATA_QUALITY: 'Data quality',
  SERVICER_CORRECTION_REQUESTED: 'Servicer correction requested',
  SERVICER_VS_BANK: 'Servicer report vs bank',
  WATERFALL_VARIANCE: 'Waterfall variance',
  WATERFALL_VS_AGENT: 'Waterfall vs agent statement',
  // Roll states (facility Monitoring roll-rate matrix)
  CURRENT: 'Current',
  LATE_30: '30–59 days late',
  LATE_60: '60–89 days late',
  LATE_90_PLUS: '90+ days late',
  DEFAULTED: 'Defaulted',
  PAID_OFF: 'Paid off',
  CHARGED_OFF: 'Charged off',
  NEW: 'New to the pool',
  // Freshness and authority words
  NOT_RECORDED: 'Not recorded',
  NOT_AVAILABLE: 'Not available',
  NOT_ASSESSED: 'Not assessed',
  STALE_AUTHORITY: 'Stale authority',
};
const ACRONYMS = new Set(['ABF', 'ACH', 'BBC', 'CDR', 'CPR', 'DDTL', 'DQ', 'DTI', 'EOD', 'FICO', 'IBOR', 'IC', 'LTV', 'NAV', 'OC', 'OID', 'PIK', 'SLA', 'SWIFT', 'UPB', 'WAC', 'WAL', 'WARF', 'DSCR', 'LLC', 'LP', 'SPV']);
export const labelFor = (token: string | null | undefined): string => {
  if (token === null || token === undefined) return '';
  const raw = String(token).trim();
  if (!raw) return '';
  const key = raw.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toUpperCase();
  if (LABEL_ALIASES[key]) return LABEL_ALIASES[key];
  if (TOKEN_ALIASES[key]) return TOKEN_ALIASES[key];
  if (!/^[A-Z0-9_]+$/.test(key)) return raw;
  const words = key.split('_').filter(Boolean).map((word, index) =>
    ACRONYMS.has(word) ? word : index === 0 ? word.charAt(0) + word.slice(1).toLowerCase() : word.toLowerCase());
  return words.join(' ');
};

/** Keep the object key, drop the bucket and account: "s3://bucket-9027…/a/b/file.csv" → "…/a/b/file.csv". */
export const shortStorageUri = (uri: string | null | undefined): string => {
  if (!uri) return '';
  const s = String(uri);
  const m = s.match(/^(s3|gs|abfs|file):\/\/[^/]+\/(.+)$/i);
  if (!m) return s;
  const key = m[2];
  const parts = key.split('/');
  return parts.length > 3 ? `…/${parts.slice(-3).join('/')}` : key;
};

/** Zero never wears a sign: "-0.00 pts" → "0.00 pts". */
export const signedPts = (value: number | null | undefined, digits = 2): string => {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return '—';
  const n = Number(value);
  const rounded = Number(n.toFixed(digits));
  if (rounded === 0) return `${(0).toFixed(digits)} pts`;
  return `${rounded > 0 ? '+' : '−'}${Math.abs(rounded).toFixed(digits)} pts`;
};

/**
 * A difference with its sign and unit: "+$1,250", "−$1,250", "$0" when the rounded value is zero.
 * `kind` picks the unit; money is the default. Zero never wears a sign.
 */
export const fmtSigned = (
  value: number | string | null | undefined,
  options: { kind?: 'money' | 'pct' | 'number'; currency?: string | null; digits?: number; fallback?: string } = {},
): string => {
  const { kind = 'money', currency = 'USD', fallback = '—' } = options;
  if (value === null || value === undefined || value === '') return fallback;
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  const digits = options.digits ?? (kind === 'money' ? 0 : 2);
  const rounded = Number(n.toFixed(digits));
  const magnitude = Math.abs(rounded);
  const body = kind === 'money'
    ? fmtMoney(magnitude, currency || 'USD', digits)
    : kind === 'pct'
      ? `${magnitude.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits })}%`
      : magnitude.toLocaleString('en-US', { maximumFractionDigits: digits });
  if (rounded === 0) return body;
  return `${rounded > 0 ? '+' : '−'}${body}`;
};

/** "12.34%" — a percentage already expressed in points (12.34 means 12.34%), with the sign the value carries. */
export const fmtPct = (value: number | string | null | undefined, digits = 2, fallback = '—'): string => {
  if (value === null || value === undefined || value === '') return fallback;
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return `${n.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits })}%`;
};

/** "1,234.5" — a plain number with thousands separators and at most `digits` decimals. */
export const fmtNumber = (value: number | string | null | undefined, digits = 2, fallback = '—'): string => {
  if (value === null || value === undefined || value === '') return fallback;
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return n.toLocaleString('en-US', { maximumFractionDigits: digits });
};

/** A stored value as text with underscores as spaces, or the fallback sentence when absent. */
export const displayText = (value: unknown, fallback = 'Not available'): string =>
  value === null || value === undefined || value === '' ? fallback : String(value).replace(/_/g, ' ');

/**
 * An AuthorityBadge value that reads as a sentence fragment: the badge prints "LABEL: VALUE", so
 * an absent authority (NONE, NOT_AVAILABLE, empty) becomes "not recorded" instead of
 * "Certificate: NOT AVAILABLE" beside a "Not available" figure (F-LR-045).
 */
export const authorityValue = (authority: string | null | undefined): string => {
  const a = String(authority || '').trim().toUpperCase();
  return a === '' || a === 'NONE' || a === 'NOT_AVAILABLE' ? 'NOT_RECORDED' : a;
};

const daysBetween = (iso: string, today: Date): number => {
  const day = iso.match(/^(\d{4}-\d{2}-\d{2})/);
  const then = day ? new Date(`${day[1]}T00:00:00`) : new Date(iso);
  if (Number.isNaN(then.getTime())) return 0;
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const thenDay = new Date(then.getFullYear(), then.getMonth(), then.getDate());
  return Math.max(0, Math.round((start.getTime() - thenDay.getTime()) / 86_400_000));
};

export interface FreshnessInput {
  asOfDate?: string | null;
  authority?: string | null;
  freshnessStatus?: string | null;
  freshnessDays?: number | null;
  freshnessAssessedAt?: string | null;
}

/**
 * The collateral freshness pair as one sentence (F-LR-044): "No tape released yet",
 * "Tape 38 days old, not yet assessed", "Assessed 12 days ago, current", "Assessed today, stale".
 * The backend sends NOT_ASSESSED with the tape's age in days when no monitoring run has looked at
 * it, and the run's status with the age it observed when one has.
 */
export const freshnessSentence = (collateral: FreshnessInput, today: Date = new Date()): string => {
  const status = String(collateral.freshnessStatus || '').toUpperCase();
  const authority = String(collateral.authority || '').toUpperCase();
  if (!collateral.asOfDate || status === 'NOT_AVAILABLE' || authority === 'NONE' || authority === 'NOT_AVAILABLE') return 'No tape released yet';
  const days = collateral.freshnessDays;
  const age = days === null || days === undefined ? null : `${plural(days, 'day')} old`;
  if (status === '' || status === 'NOT_ASSESSED') return age ? `Tape ${age}, not yet assessed` : 'Tape not yet assessed';
  const assessedAgo = collateral.freshnessAssessedAt ? daysBetween(collateral.freshnessAssessedAt, today) : null;
  const assessed = assessedAgo === null ? 'Assessed' : assessedAgo === 0 ? 'Assessed today' : `Assessed ${plural(assessedAgo, 'day')} ago`;
  if (status === 'CURRENT' || status === 'FRESH') return `${assessed}, current`;
  if (status.startsWith('STALE')) return `${assessed}, stale`;
  return `${assessed}, ${humanizeToken(status).toLowerCase()}`;
};
